import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DialogComponent } from '@syncfusion/ej2-react-popups';
import { TextBoxComponent } from '@syncfusion/ej2-react-inputs';
import { ButtonComponent } from '@syncfusion/ej2-react-buttons';
import type { RoleDto } from '../../api/models/RoleDto';
import type { PermissionDto } from '../../api/models/PermissionDto';
// import type { RolePermissionDto } from '../../api/models/RolePermissionDto';
import { PermissionControllerService } from '../../api/services/PermissionControllerService';
import { RoleControllerService } from '../../api/services/RoleControllerService';
import { useErrorHandler } from '../../utils/errorHandler';
import { useToast } from '../../hooks/useToast';
import type { CrudFormProps } from '../../components/CrudPage';
import { SelectableListComponent, type SelectableItem } from '../../components/SelectableListComponent';

interface PermissionWithSelection extends PermissionDto, SelectableItem {
  id: string; // Add id field for frontend compatibility
}

export default function UserRoleFormDialog({ value, onClose, onSave }: CrudFormProps<RoleDto & { id: string }>) {
  const { t } = useTranslation(['common']);
  const { handleError } = useErrorHandler();
  const { showToast } = useToast();
  
  // Add styles to document head to avoid React DOM manipulation issues
  React.useEffect(() => {
    const styleId = 'user-role-dialog-styles';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        .user-role-dialog .e-dlg-content {
          padding: 0 !important;
          overflow: hidden !important;
          display: flex !important;
          flex-direction: column !important;
          height: 100% !important;
        }
        .user-role-dialog .e-dlg-content > div {
          height: 100% !important;
          overflow: hidden !important;
          display: flex !important;
          flex-direction: column !important;
        }
      `;
      document.head.appendChild(style);
    }
  }, []);
  
  const [visible, setVisible] = useState(false);
  const [roleData, setRoleData] = useState<RoleDto & { id: string }>({
    id: '',
    name: '',
    description: '',
    tenantId: '',
    tenantName: '',
    isSystem: false
  });

  const isSystemRole = Boolean(roleData.isSystem);
  
  const [permissions, setPermissions] = useState<PermissionWithSelection[]>([]);
  const [filteredPermissions, setFilteredPermissions] = useState<PermissionWithSelection[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (isMountedRef.current) {
      const initializeDialog = async () => {
        // First load all permissions
        await loadPermissions();
        
        // Then load role-specific permissions if editing
        if (value) {
          setRoleData(value);
          if (value.id) {
            // Wait a bit to ensure permissions state is updated
            await new Promise(resolve => setTimeout(resolve, 100));
            await loadRolePermissions(value.id);
          }
        }
        setVisible(true);
      };
      
      initializeDialog();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadPermissions and loadRolePermissions are local functions; including them would trigger on every render
  }, [value]);

  // Initialize filteredPermissions when permissions change (only once)
  // Use a ref to track if we've already initialized
  const filteredPermissionsInitializedRef = useRef(false);
  useEffect(() => {
    if (permissions.length > 0 && !filteredPermissionsInitializedRef.current) {
      filteredPermissionsInitializedRef.current = true;
      setFilteredPermissions(permissions);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- depends on length only, not the array itself, to avoid infinite loop
  }, [permissions.length]);


  // Handler functions - MUST be defined before useCallback that uses them
  const handleInputChange = useCallback((field: keyof RoleDto, value: string) => {
    if (isMountedRef.current) {
      setRoleData(prev => ({ ...prev, [field]: value }));
    }
  }, []);

  const handlePermissionsChange = useCallback((updatedPermissions: PermissionWithSelection[]) => {
    if (isMountedRef.current) {
      // Only update if permissions actually changed to avoid infinite loops
      setPermissions(prev => {
        // Check if anything actually changed
        if (prev.length !== updatedPermissions.length) {
          return [...updatedPermissions];
        }
        const hasChanged = prev.some((p, i) => {
          const updated = updatedPermissions[i];
          return !updated || p.id !== updated.id || p.selected !== updated.selected;
        });
        if (!hasChanged) {
          return prev; // Return same reference to prevent re-render
        }
        return [...updatedPermissions];
      });
    }
  }, []);

  const handlePermissionSelect = useCallback((_permission: PermissionWithSelection, _selected: boolean) => {
    // Selection handled by handlePermissionsChange
  }, []);

  // Stable callback for filtered items change to avoid infinite loops
  const handleFilteredItemsChange = useCallback((filtered: PermissionWithSelection[]) => {
    // Only update if actually changed to avoid infinite loops
    setFilteredPermissions(prev => {
      if (prev.length !== filtered.length) {
        return filtered;
      }
      const hasChanged = prev.some((p, i) => {
        const f = filtered[i];
        return !f || p.id !== f.id;
      });
      return hasChanged ? filtered : prev;
    });
  }, []);

  // Dialog content callback - MUST be called before any early returns (React hooks rule)
  const dialogContent = useCallback(() => {
    return (
      <div className="p-4 user-role-dialog-content" style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
        {isSystemRole && (
          <div className="mb-4 px-3 py-2 rounded border border-blue-300 bg-blue-50 text-blue-800 text-sm">
            {t('userRole.form.systemReadOnly')}
          </div>
        )}
        <div className="mb-4 flex-shrink-0">
          <div className="mb-3">
            <label className="block text-sm font-medium mb-1">
              {t('userRole.form.name')} *
            </label>
            <TextBoxComponent
              value={roleData.name || ''}
              onChange={(e: any) => handleInputChange('name', e.value)}
              placeholder={t('userRole.form.namePlaceholder')}
              cssClass="w-full"
              enabled={!isSystemRole}
            />
          </div>

          <div className="mb-3">
            <label className="block text-sm font-medium mb-1">
              {t('userRole.form.description')}
            </label>
            <TextBoxComponent
              value={roleData.description || ''}
              onChange={(e: any) => handleInputChange('description', e.value)}
              placeholder={t('userRole.form.descriptionPlaceholder')}
              multiline={true}
              cssClass="w-full"
              enabled={!isSystemRole}
            />
          </div>
        </div>

        <div className="flex-1 flex flex-col min-h-0" style={{ display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
          <div className="mb-2 flex-shrink-0">
            <h6 className="text-lg font-semibold">{t('permissions.title')}</h6>
          </div>
          
          <div style={{ flex: '1 1 0%', minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <SelectableListComponent
              items={permissions}
              onSelectionChange={handlePermissionsChange}
              getItemId={(p) => p.id}
              getItemLabel={(p) => p.action || 'Unknown Action'}
              getItemDescription={(p) => p.description || 'No description'}
              searchPlaceholder={t('permissions.searchPlaceholder')}
              searchFields={['action', 'description']}
              height="100%"
              showSelectAll={false} // Buttons are in footer
              showSummary={false} // Summary is in footer
              showOnlySelectedFilter={true}
              loading={loading}
              emptyMessage={t('permissions.noPermissionsAvailable')}
              noResultsMessage={t('permissions.noPermissionsFound')}
              onItemSelect={handlePermissionSelect}
              onFilteredItemsChange={handleFilteredItemsChange}
            />
          </div>
        </div>
      </div>
    );
  }, [roleData, handleInputChange, t, permissions, loading, handlePermissionsChange, handlePermissionSelect, handleFilteredItemsChange, isSystemRole]);

  const loadPermissions = async () => {
    if (!isMountedRef.current) return;
    
    try {
      setLoading(true);
      const allPermissions = await PermissionControllerService.findAll();
      if (isMountedRef.current) {
        const permissionsWithSelection = (allPermissions || [])
          .filter(p => p && typeof p.uuid === 'string' && p.uuid.length > 0)
          .map(p => ({
            ...p,
            id: p.uuid || '',
            selected: false,
            action: p.action || 'Unknown Action',
            description: p.description || 'No description'
          }))
          // Sort alphabetically by action (case-insensitive)
          .sort((a, b) => (a.action || '').toLowerCase().localeCompare((b.action || '').toLowerCase()));
        setPermissions(permissionsWithSelection);
      }
    } catch (error) {
      if (isMountedRef.current) {
        console.error('Error loading permissions:', error);
        handleError(error, 'loading permissions');
        // Set empty array as fallback
        setPermissions([]);
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  };

  const loadRolePermissions = async (roleId: string) => {
    if (!roleId || !isMountedRef.current) return;
    
    try {
      const rolePermissions = await RoleControllerService.getRolePermissions(roleId);
      if (isMountedRef.current) {
        const selectedPermissionIds = rolePermissions
          .map(rp => rp.permissionId)
          .filter((id): id is string => id !== undefined && id !== null);
        
        // Wait for permissions to be loaded if they're not yet available
        if (permissions.length === 0) {
          await new Promise(resolve => setTimeout(resolve, 200));
        }
        
        setPermissions(prev => {
          const updated = prev.map(p => ({
            ...p,
            selected: Boolean(selectedPermissionIds.includes(p.id))
          }));
          return updated;
        });
      }
    } catch (error) {
      if (isMountedRef.current) {
        console.error('Error loading role permissions:', error);
        handleError(error, 'loading role permissions');
      }
    }
  };


  // Calculate selected count for summary (memoized for performance)
  const selectedCount = useMemo(() => {
    return permissions.filter(p => Boolean(p.selected)).length;
  }, [permissions]);

  const handleSelectAll = useCallback(() => {
    try {
      // Use filtered permissions (visible items after search/filter)
      // If filteredPermissions is empty or same as permissions, select all
      const visiblePermissionIds = filteredPermissions.length > 0 && filteredPermissions.length < permissions.length
        ? filteredPermissions.map(p => p.id).filter(Boolean)
        : permissions.map(p => p.id).filter(Boolean);
      
      setPermissions(prev => prev.map(p => ({
        ...p,
        selected: visiblePermissionIds.includes(p.id) ? true : Boolean(p.selected)
      })));
    } catch (error) {
      console.error('Error in handleSelectAll:', error);
    }
  }, [filteredPermissions, permissions]);

  const handleDeselectAll = useCallback(() => {
    try {
      // Use filtered permissions (visible items after search/filter)
      // If filteredPermissions is empty or same as permissions, deselect all
      const visiblePermissionIds = filteredPermissions.length > 0 && filteredPermissions.length < permissions.length
        ? filteredPermissions.map(p => p.id).filter(Boolean)
        : permissions.map(p => p.id).filter(Boolean);
      
      setPermissions(prev => prev.map(p => ({
        ...p,
        selected: visiblePermissionIds.includes(p.id) ? false : Boolean(p.selected)
      })));
    } catch (error) {
      console.error('Error in handleDeselectAll:', error);
    }
  }, [filteredPermissions, permissions]);

  const handleSave = async () => {
    if (!isMountedRef.current) return;
    
    try {
      setSaving(true);
      
      // Get the role UUID BEFORE saving (for existing roles)
      // Note: roleData.id is actually the UUID, mapped as 'id' for CrudPage compatibility
      let roleId: string | undefined = roleData.id && roleData.id.length > 0 ? roleData.id : undefined;
      
      // Save role details - onSave should return the saved role with UUID (mapped as 'id')
      const savedRole = await onSave(roleData);
      
      // Get the role UUID from the saved role (this is important for new roles)
      // The savedRole.id is actually the UUID from the backend
      if (savedRole && (savedRole as any).id) {
        roleId = (savedRole as any).id; // This is the UUID, just mapped as 'id'
      } else if (!roleId && roleData.id && roleData.id.length > 0) {
        // Fallback to existing UUID if saved role doesn't have one
        roleId = roleData.id; // This is the UUID, just mapped as 'id'
      }
      
      // If still no ID (new role), try to find it by name
      // Note: roleId is actually the UUID, just mapped as 'id' for CrudPage compatibility
      if (!roleId || roleId.length === 0) {
        const allRoles = await RoleControllerService.findAll();
        const newRole = allRoles.find(r => r.name === roleData.name && r.uuid);
        if (newRole && newRole.uuid) {
          roleId = newRole.uuid; // Use UUID directly from backend
        }
      }
      
      // Save role permissions if we have a role ID
      if (roleId && typeof roleId === 'string' && roleId.length > 0) {
        const selectedPermissionIds = permissions
          .filter(p => p.selected && typeof p.id === 'string')
          .map(p => p.id);
        
        await RoleControllerService.updateRolePermissions(roleId, selectedPermissionIds);
      } else {
        console.error('Cannot save role permissions: no roleId available', { 
          roleId, 
          roleData, 
          savedRole,
          'savedRole.id': savedRole ? (savedRole as any).id : 'no savedRole',
          'roleData.id': roleData.id
        });
        throw new Error('Cannot save role permissions: no role ID available after saving role');
      }
      
      // Only close dialog after everything is saved successfully
      // Show success toast here, after ALL operations (role + permissions) are complete
      if (isMountedRef.current) {
        // Show success message after everything is saved
        showToast('success', t('actions.edit'), t('messages.updateSuccess'));

        setVisible(false);
        onClose();
      }
    } catch (error) {
      if (isMountedRef.current) {
        console.error('Error saving role:', error);
        handleError(error, 'saving role');
        throw error; // Re-throw to keep dialog open on error
      }
    } finally {
      if (isMountedRef.current) {
        setSaving(false);
      }
    }
  };

  const handleClose = () => {
    if (isMountedRef.current) {
      setVisible(false);
      onClose();
    }
  };

  const handleCancel = () => {
    if (isMountedRef.current) {
      setVisible(false);
      onClose();
    }
  };

  // Don't render if not mounted
  if (!isMountedRef.current) {
    return null;
  }

  // Don't render if permissions are still loading and we don't have any permissions yet
  if (loading && permissions.length === 0) {
    return (
      <DialogComponent
        width="800px"
        isModal={true}
        visible={visible}
        close={handleClose}
        header={value ? t('userRole.form.editTitle') : t('userRole.form.createTitle')}
        beforeClose={(e) => {
          e.cancel = true;
          handleClose();
        }}
      >
        <div className="p-4 text-center">
          <div className="text-lg">{t('common.loading')}</div>
          <div className="text-sm text-gray-500 mt-2">{t('permissions.loadingPermissions')}</div>
        </div>
      </DialogComponent>
    );
  }

  // Additional safety check - don't render if permissions array is not properly initialized
  if (!Array.isArray(permissions)) {
    console.warn('Permissions array not properly initialized:', { permissions });
    return (
      <DialogComponent
        width="800px"
        isModal={true}
        visible={visible}
        close={handleClose}
        header={value ? t('userRole.form.editTitle') : t('userRole.form.createTitle')}
        beforeClose={(e) => {
          e.cancel = true;
          handleClose();
        }}
      >
        <div className="p-4 text-center">
          <div className="text-lg text-red-600">Error</div>
          <div className="text-sm text-gray-500 mt-2">Failed to initialize permissions properly</div>
        </div>
      </DialogComponent>
    );
  }

  return (
    <DialogComponent
        width="800px"
        height="90vh"
        cssClass="user-role-dialog"
        isModal={true}
        visible={visible}
        close={handleClose}
        header={value ? t('userRole.form.editTitle') : t('userRole.form.createTitle')}
        beforeClose={(e) => {
          e.cancel = true;
          handleClose();
        }}
        footerTemplate={() => (
        <div className="p-4 border-t border-gray-200">
          {/* Select/Deselect All Buttons */}
          <div className="flex justify-between items-center mb-3">
            <div className="flex gap-2">
              <ButtonComponent
                type="button"
                onClick={handleSelectAll}
                cssClass="e-outline e-small"
              >
                {t('actions.selectAll')}
              </ButtonComponent>
              <ButtonComponent
                type="button"
                onClick={handleDeselectAll}
                cssClass="e-outline e-small"
              >
                {t('actions.deselectAll')}
              </ButtonComponent>
            </div>
            <span className="text-sm text-gray-500">
              <strong>{selectedCount}</strong> von <strong>{permissions.length}</strong> {selectedCount === 1 ? 'ausgewählt' : 'ausgewählt'}
            </span>
          </div>
          
          {/* Save/Cancel Buttons */}
          <div className="flex justify-end gap-2">
            <ButtonComponent
              type="button"
              onClick={handleCancel}
              cssClass="e-outline"
            >
              {t('actions.cancel')}
            </ButtonComponent>
            <ButtonComponent
              type="button"
              onClick={handleSave}
              isPrimary={true}
              disabled={saving || isSystemRole}
            >
              {saving ? t('actions.saving') : t('actions.save')}
            </ButtonComponent>
          </div>
        </div>
      )}
    >
      {dialogContent()}
    </DialogComponent>
  );
} 